import { Chip } from "@heroui/react";

import { useTranslations } from "@/src/i18n/i18n";
import type { PostStatus } from "@/src/pages/Posts/posts.type";
import { STATUS_KEY } from "@/src/pages/Posts/posts.type";

/**
 * Le ton de chaque statut.
 *
 * ⚠️ **`pending_review` est en `accent`, pas en `warning`.** L'ambre est déjà
 * celui de « non jugé » sur la pastille de score, juste à côté sur la même
 * ligne : deux ambres voisins qui disent des choses différentes ne se
 * distinguent plus. `scanning`, lui, mérite l'ambre — une publication qui y
 * reste est une anomalie, pas une file.
 */
const STATUS_COLOR = {
  pending_review: "accent",
  scanning: "warning",
  approved: "success",
  rejected: "danger",
} as const satisfies Record<
  PostStatus,
  "accent" | "warning" | "success" | "danger"
>;

/**
 * Le statut d'une publication, lu pareil sur la liste et sur la fiche.
 *
 * Même parti qu'`EventStatusChip`, et pour la même raison : **les codes ne
 * disent pas ce que leur nom suggère** (`scanning` = la machine regarde,
 * `pending_review` = c'est à un humain de jouer, `rejected` = retirée par un
 * administrateur), et deux tables de libellés auraient fini par nommer le même
 * code différemment d'un écran à l'autre.
 */
export function PostStatusChip({ status }: { status: PostStatus }) {
  const t = useTranslations("posts");

  return (
    <Chip color={STATUS_COLOR[status]} size="sm">
      {t(STATUS_KEY[status])}
    </Chip>
  );
}
